/**
 * Collections API - Members Management
 *
 * Add, update, remove, and list collection members.
 */

import { z } from 'zod'
import { PaginationMetaSchema } from '../common'
import { CollectionMemberSchema, CollectionMemberRoleSchema } from './common'

// ============================================================================
// GET /v1/collections/:id/members
// ============================================================================

export const ListCollectionMembersRequestSchema = z.object({
  limit: z.coerce.number().positive().max(100).optional(),
  offset: z.coerce.number().min(0).optional(),
  role: CollectionMemberRoleSchema.optional(),
  searchQuery: z.string().optional(),
  orderBy: z.enum(['joinedAt', 'name']).optional(),
  orderDirection: z.enum(['asc', 'desc']).optional()
})

export type ListCollectionMembersRequest = z.infer<typeof ListCollectionMembersRequestSchema>

export const ListCollectionMembersResponseSchema = z.object({
  data: z.array(CollectionMemberSchema),
  message: z.string(),
  meta: PaginationMetaSchema
})

export type ListCollectionMembersResponse = z.infer<typeof ListCollectionMembersResponseSchema>

// ============================================================================
// POST /v1/collections/:id/members
// ============================================================================

export const AddCollectionMembersRequestSchema = z.object({
  members: z
    .array(
      z.object({
        userId: z.string(),
        role: CollectionMemberRoleSchema.optional(),
        canUpload: z.boolean().optional(),
        canDelete: z.boolean().optional(),
        canManageMembers: z.boolean().optional(),
        canModerate: z.boolean().optional(),
        notifyOnNewMedia: z.boolean().optional(),
        notifyOnComments: z.boolean().optional(),
        notifyOnTags: z.boolean().optional()
      })
    )
    .min(1)
    .max(50)
})

export type AddCollectionMembersRequest = z.infer<typeof AddCollectionMembersRequestSchema>

export const AddCollectionMembersResponseSchema = z.object({
  data: z.object({
    added: z.number(),
    skipped: z.number()
  }),
  message: z.string()
})

export type AddCollectionMembersResponse = z.infer<typeof AddCollectionMembersResponseSchema>

// ============================================================================
// PATCH /v1/collections/:collectionId/members/:memberId
// ============================================================================

export const UpdateCollectionMemberRequestSchema = z.object({
  role: CollectionMemberRoleSchema.optional(),
  canUpload: z.boolean().optional(),
  canDelete: z.boolean().optional(),
  canManageMembers: z.boolean().optional(),
  canModerate: z.boolean().optional(),
  notifyOnNewMedia: z.boolean().optional(),
  notifyOnComments: z.boolean().optional(),
  notifyOnTags: z.boolean().optional()
})

export type UpdateCollectionMemberRequest = z.infer<typeof UpdateCollectionMemberRequestSchema>

export const UpdateCollectionMemberResponseSchema = z.object({
  data: CollectionMemberSchema,
  message: z.string()
})

export type UpdateCollectionMemberResponse = z.infer<typeof UpdateCollectionMemberResponseSchema>

// ============================================================================
// DELETE /v1/collections/:collectionId/members/:memberId
// ============================================================================

export const RemoveCollectionMemberResponseSchema = z.object({
  data: z.object({ success: z.boolean() }),
  message: z.string()
})

export type RemoveCollectionMemberResponse = z.infer<typeof RemoveCollectionMemberResponseSchema>
